import { useEffect } from 'react';
import { useRollbar } from '@rollbar/react';
import { useQuery } from '@tanstack/react-query';
import LogRocket from 'logrocket';

import { type Session } from '@/types/session';
import { queryKeys } from '@/hooks/api/queryKeys';
import { analytics } from '@/hooks/useSegment';
import { axiosInstance as axios } from '@/utils/axiosConfig';

export const sessionDefault: Session = {
  account: {
    id: '',
    name: '',
    slug: '',
    status: '',
    settings: {
      hipaa: {
        enabled: false,
        region: null
      }
    },
    productPlan: {
      id: ''
    }
  },
  user: {
    id: '',
    email: '',
    firstName: '',
    lastName: '',
    avatarUri: '',
    isTwoFactorAuthEnabled: false,
    isGoogleUser: false,
    hasSetPassword: false
  }
};

export async function getSession() {
  const endpoint = '/v1/dashboard/session/state';

  const { data } = await axios.get<{ session: Session }>(endpoint, {
    withCredentials: true
  });

  return data.session;
}

export function useSessionQuery() {
  const rollbar = useRollbar();

  const query = useQuery({
    queryKey: [queryKeys.session],
    queryFn: getSession,

    // prevent fetching the data each time this hook is called
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,

    // refetch in the background every 8 hours
    staleTime: 1000 * 60 * 60 * 8,
    placeholderData: sessionDefault
  });

  useEffect(() => {
    const initializeThirdPartyIntegrations = async (session: Session) => {
      const name = `${session.user.firstName} ${session.user.lastName}`;

      if (analytics) {
        await analytics.identify(session.user.id, {
          email: session.user.email
        });
      }

      if (import.meta.env.PUBLIC_LOGROCKET_APP_ID && !session.account.settings?.hipaa?.enabled) {
        LogRocket.identify(session.user.id, {
          name,
          email: session.user.email,
          account_id: session.account.id,
          plan_id: session.account.productPlan.id
        });
      }

      if (import.meta.env.PUBLIC_ROLLBAR_ACCESS_TOKEN) {
        rollbar.configure({
          payload: {
            person: {
              id: session.user.id,
              email: session.user.email
            }
          }
        });

        if (import.meta.env.PUBLIC_LOGROCKET_APP_ID) {
          LogRocket.getSessionURL((sessionURL) => {
            rollbar.configure({
              transform: (obj) => {
                // eslint-disable-next-line no-param-reassign
                obj.sessionURL = sessionURL;
              }
            });
          });
        }
      }
    };

    if (query.data && query.data.user.id !== '') {
      void initializeThirdPartyIntegrations(query.data);
    } else if (analytics) {
      void analytics.reset();
    }
  }, [query.data, rollbar]);

  return query;
}
